import { Component, inject, computed, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { catchError, of, map, tap, switchMap } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { CmsCollection, CmsOffer } from '@fcom/core-api';
import {
  PageMetaService,
  PriceCalendarCTAParams,
  PriceCalendarService,
  PromotionItem,
  PromotionType,
} from '@fcom/common';
import { GlobalBookingTravelClass } from '@fcom/core/interfaces/global-booking-travel-class.interface';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { getOfferWithAmDestination } from '@fcom/common/utils/am-offer.utils';
import { AmDestinationService } from '@fcom/destination-search/services/am-destination.service';
import { LocationRouteCffService, stopPropagation } from '@fcom/core';

import { BlockOffer } from '../../interfaces/destination-promo.interface';
import { toPromoBlock } from '../../services/template/data-mappers';

@Component({
  selector: 'cms-offers-grid',
  template: `
    @if (originLocationCode()) {
      <fin-offer-grid
        [title]="content().teaserTitle"
        [offers]="offers()"
        [cmsTags]="content().subjectTaxonomyTags || []"
        (flightHotelClicked)="openAmContinueModal($event.amLink)"
        (calendarClicked)="calendarClicked(originLocationCode(), $event)"
        [tripType]="tripType()"
      />
    }
    <fin-am-destination-continue-modal
      [open]="isAmContinueModalOpen()"
      (closeModal)="closeAmContinueModal()"
      [bookUrl]="flightHotelUrl()"
    />
    <fcom-modal
      [open]="calendarOpen() !== null"
      [fitToContent]="true"
      [buttons]="null"
      [roundedContent]="true"
      [fullHeight]="true"
      [extraWide]="true"
      (close)="closePriceCalendar()"
      data-testid="price-calendar-modal"
    >
      <fin-price-calendar
        [openWithParams$]="calendarOpen$"
        [showAddReturn]="true"
        [showSubtitle]="true"
        [ctaLabel]="'bookingSearch.search' | finLocalization"
        (modalClose)="closePriceCalendar()"
        (ctaClicked)="priceCalendarCTAClicked($event)"
      />
    </fcom-modal>
  `,
  standalone: false,
})
export class CmsOffersGridComponent {
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private amDestinationService = inject(AmDestinationService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private priceCalendarService = inject(PriceCalendarService);
  private pageMetaService = inject(PageMetaService);

  content = input<CmsCollection>();

  readonly flightHotelUrl = signal<string>(undefined);
  readonly isAmContinueModalOpen = signal(false);
  readonly calendarOpen$ = this.priceCalendarService.calendarOpen$;
  readonly calendarOpen = toSignal(this.calendarOpen$, { initialValue: null });
  readonly originLocationCode = toSignal(this.pageMetaService.defaultOriginLocationCode$);

  readonly tripType = computed<TripType>(() => {
    const tripType = this.content().localSettings?.tripType as string | undefined;
    return TripType[tripType?.toUpperCase()] ?? TripType.RETURN;
  });

  readonly offers = toSignal(
    toObservable(this.content).pipe(
      switchMap((content) => {
        return this.languageService.lang.pipe(
          map((lang) => content.items.map((offer) => toPromoBlock(offer as CmsOffer, lang))),
          switchMap((offers) =>
            this.languageService.localeValue.includes('FI')
              ? getOfferWithAmDestination(
                  offers,
                  this.amDestinationService.amDestinationList(),
                  this.locationRouteCffService
                )
              : of(offers)
          ),
          tap((offers) => {
            const promotionItems: PromotionItem[] = offers.map((offer, index) => ({
              position: `${index}`,
              id: 'top-destinations',
              product: offer.destination,
              type: 'static',
            }));

            this.gtmService.internalPromotion(promotionItems, PromotionType.VIEW);
          }),
          catchError(() => {
            return of(undefined);
          })
        );
      })
    )
  );

  openAmContinueModal(flightHotelUrl: string): void {
    this.isAmContinueModalOpen.set(true);
    this.flightHotelUrl.set(flightHotelUrl);
  }

  closeAmContinueModal(): void {
    this.isAmContinueModalOpen.set(false);
  }

  calendarClicked(
    selectedOriginLocationCode: string,
    [event, destinationItem, travelClass, tripType]: [Event, BlockOffer, GlobalBookingTravelClass, TripType, number]
  ): void {
    stopPropagation(event);
    this.priceCalendarService.openPriceCalendar(
      selectedOriginLocationCode,
      destinationItem.destination,
      tripType,
      travelClass,
      destinationItem.title,
      destinationItem.discountCode
    );
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
  }

  closePriceCalendar(): void {
    this.priceCalendarService.closePriceCalendar();
  }
}
